// Server state through TanStack Query: one read hook and one write hook over the generated client. Writes carry an
// idempotency key per attempt, invalidate the reads they touch, and surface problem+json as AppError for the form.
import { useRef } from "react";
import { matchQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { AppError, newIdempotencyKey } from "@/api/runtime";
import { ERROR_LABELS } from "@/mc/labels";

export function useRead<T>(key: readonly unknown[], fn: () => Promise<T>, opts: Partial<UseQueryOptions<T, AppError>> = {}) {
  return useQuery<T, AppError>({
    queryKey: key, queryFn: fn, retry: false, staleTime: 15_000, ...opts,
    // A refusal is settled state. Mounting another observer must not restart it and
    // bounce an owning guard between its skeleton and children (ECC E6-1/F6-1).
    retryOnMount: false,
    gcTime: Math.max(opts.gcTime ?? 0, 5 * 60_000),
    refetchOnMount: (query) => isClientRefusal(query.state.error) ? false
      : typeof opts.refetchOnMount === "function" ? opts.refetchOnMount(query) : opts.refetchOnMount ?? true,
    refetchOnWindowFocus: (query) => isClientRefusal(query.state.error) ? false
      : typeof opts.refetchOnWindowFocus === "function" ? opts.refetchOnWindowFocus(query) : opts.refetchOnWindowFocus ?? false,
    refetchOnReconnect: (query) => isClientRefusal(query.state.error) ? false
      : typeof opts.refetchOnReconnect === "function" ? opts.refetchOnReconnect(query) : opts.refetchOnReconnect ?? true,
    refetchInterval: (query) => isClientRefusal(query.state.error) ? false
      : typeof opts.refetchInterval === "function" ? opts.refetchInterval(query) : opts.refetchInterval ?? false,
  });
}

export function isClientRefusal(error: AppError | null | undefined): boolean {
  return !!error && error.status >= 400 && error.status < 500;
}

/**
 * The reads every successful write refreshes, whatever the caller names: the navigation's badges, the Action Center
 * and Home. An act that resolves an action closes its item on the server (auto-close, DC-46/47), and the person sees it
 * leave without waiting for the next poll.
 */
export const ALWAYS_INVALIDATE: readonly (readonly unknown[])[] = [["shell-nav"], ["action-center"], ["home"]];

export function useWrite<TIn, TOut>(fn: (input: TIn, key: string) => Promise<TOut>, invalidate: readonly (readonly unknown[])[] = []) {
  const qc = useQueryClient();
  const pending = useRef(new Map<string | undefined, Promise<TOut>>());
  const mutation = useMutation<TOut, AppError, TIn>({
    mutationFn: (input) => fn(input, newIdempotencyKey()),
    onSuccess: async () => {
      // A query may match several prefixes. Invalidate their union once so an
      // overlapping key cannot cancel and restart the same in-flight refresh.
      const keys = [...invalidate, ...ALWAYS_INVALIDATE];
      await qc.invalidateQueries({ predicate: query => keys.some(queryKey => matchQuery({ queryKey }, query)) });
    },
  });
  const mutateAsync: typeof mutation.mutateAsync = (...args) => {
    // These are JSON tool inputs (binary uploads use their own transport).
    // Claim the payload synchronously, before React can publish isPending, and
    // retain it through callbacks/invalidation. A settled refusal permits retry.
    const payload = JSON.stringify(args[0]);
    const active = pending.current.get(payload);
    if (active) return active;
    const attempt = mutation.mutateAsync(...args).finally(() => pending.current.delete(payload));
    pending.current.set(payload, attempt);
    return attempt;
  };
  return { ...mutation, mutateAsync, mutate: (...args: Parameters<typeof mutation.mutate>) => { void mutateAsync(...args).catch(() => undefined); } };
}

/**
 * The one-line error (DS §2.1). A failure on the server's side (a 5xx, or INTERNAL) reads its remedy, never its own
 * detail, and names its request id once as the Correlation ID, so the person can quote it (HO-32); a refusal reads its
 * detail, then its remedy when that says more.
 */
export function errorText(e: unknown): string {
  if (e instanceof AppError) {
    if (e.status >= 500 || e.code === "INTERNAL") {
      const sentence = e.remedy || e.message;
      return e.requestId ? `${sentence} ${ERROR_LABELS.correlationId} ${e.requestId}` : sentence;
    }
    return e.remedy && e.remedy !== e.message ? `${e.message} ${e.remedy}` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
